/**
 * DOM overlay: counters, hint, win card, debug readout.
 *
 * The UI is HTML rather than WebGL text on purpose — crisp at any DPR, accessible, and
 * safe-area handling is one CSS property instead of a layout system.
 */

export interface DebugStats {
  fps: number;
  particles: number;
  magnets: number;
  physicsMs: number;
  renderMs: number;
  stepsPerFrame: number;
  resolution: number;
  drawCalls: number;
}

export class HUD {
  private readonly magnetCounter: HTMLElement;
  private readonly goalMeter: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly outcome: HTMLElement;
  private readonly outcomeEyebrow: HTMLElement;
  private readonly outcomeTitle: HTMLElement;
  private readonly outcomeNote: HTMLElement;
  private readonly debug: HTMLElement;
  private readonly intro: HTMLElement;
  private readonly introIndex: HTMLElement;
  private readonly introName: HTMLElement;
  private readonly introHint: HTMLElement;
  private readonly statTime: HTMLElement;
  private readonly statMagnets: HTMLElement;
  private readonly statGoal: HTMLElement;

  private introTimer = 0;

  private hintTimer = 0;
  private lastDebugAt = 0;

  // The game loop calls these every frame. Writing to the DOM 60 times a second for text
  // that changes twice a level is a real cost on a phone, so every setter is a no-op
  // unless the value actually moved.
  private lastMagnets = Number.NaN;
  private lastGoalPercent = Number.NaN;
  private lastOutcome: string | null = null;

  constructor(root: ParentNode = document) {
    this.magnetCounter = requireElement(root, '#magnet-counter');
    this.goalMeter = requireElement(root, '#goal-meter');
    this.hint = requireElement(root, '#hint');
    this.outcome = requireElement(root, '#outcome');
    this.outcomeEyebrow = requireElement(root, '#outcome-eyebrow');
    this.outcomeTitle = requireElement(root, '#outcome-title');
    this.outcomeNote = requireElement(root, '#outcome-note');
    this.debug = requireElement(root, '#debug');
    this.intro = requireElement(root, '#intro');
    this.introIndex = requireElement(root, '#intro .intro-index');
    this.introName = requireElement(root, '#intro .intro-name');
    this.introHint = requireElement(root, '#intro .intro-hint');
    this.statTime = requireElement(root, '#stat-time');
    this.statMagnets = requireElement(root, '#stat-magnets');
    this.statGoal = requireElement(root, '#stat-goal');
  }

  /** Title card at the start of a level. Fades itself out. */
  showIntro(index: number, name: string, hint: string, seconds = 2.6): void {
    this.introIndex.textContent = `Level ${index}`;
    this.introName.textContent = name;
    this.introHint.textContent = hint;
    this.intro.classList.remove('is-leaving');
    this.intro.hidden = false;
    // Restart the entry animation even if the card was already showing.
    void this.intro.offsetWidth;
    this.introTimer = seconds;
  }

  hideIntro(): void {
    if (this.intro.hidden || this.intro.classList.contains('is-leaving')) return;
    this.introTimer = 0;
    this.intro.classList.add('is-leaving');
    const card = this.intro;
    window.setTimeout(() => {
      if (card.classList.contains('is-leaving')) card.hidden = true;
    }, 550);
  }

  /** Fill in the results card. Times are formatted m:ss.t. */
  setResults(seconds: number, magnets: number, goalFraction: number): void {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds - minutes * 60;
    this.statTime.textContent =
      minutes > 0 ? `${minutes}:${rest.toFixed(1).padStart(4, '0')}` : `${rest.toFixed(1)}s`;
    this.statMagnets.textContent = String(magnets);
    this.statGoal.textContent = `${Math.round(Math.min(1, goalFraction) * 100)}%`;
  }

  setMagnets(remaining: number, total: number): void {
    if (remaining === this.lastMagnets) return;
    const first = Number.isNaN(this.lastMagnets);
    this.lastMagnets = remaining;
    if (!first) pop(this.magnetCounter);
    this.magnetCounter.textContent =
      remaining === 1 ? '1 magnet left' : `${remaining} magnets left`;
    this.magnetCounter.classList.toggle('is-empty', remaining === 0 && total > 0);
  }

  /** How full the goal is, 0..1. */
  setGoalProgress(fraction: number): void {
    const percent = Math.max(0, Math.min(100, Math.round(fraction * 100)));
    if (percent === this.lastGoalPercent) return;
    this.lastGoalPercent = percent;
    this.goalMeter.textContent = `goal ${percent}%`;
    this.goalMeter.classList.toggle('is-empty', false);
  }

  /** Show a transient line of guidance. */
  showHint(text: string, seconds = 4): void {
    this.hint.textContent = text;
    this.hint.classList.remove('is-hidden');
    this.hintTimer = seconds;
  }

  tickHint(dt: number): void {
    if (this.introTimer > 0) {
      this.introTimer -= dt;
      if (this.introTimer <= 0) this.hideIntro();
    }
    if (this.hintTimer <= 0) return;
    this.hintTimer -= dt;
    if (this.hintTimer <= 0) this.hint.classList.add('is-hidden');
  }

  /** Show the outcome card, or hide it with `null`. */
  setOutcome(outcome: 'solved' | 'failed' | null, note = ''): void {
    const key = `${outcome}|${note}`;
    if (key === this.lastOutcome) return;
    this.lastOutcome = key;

    if (!outcome) {
      this.outcome.hidden = true;
      return;
    }

    const solved = outcome === 'solved';
    this.outcome.classList.toggle('is-failed', !solved);
    this.outcomeEyebrow.textContent = solved ? 'Level complete' : 'Run finished';
    this.outcomeTitle.textContent = solved ? 'Solved' : 'Not there yet';
    this.outcomeNote.textContent = note;
    this.outcomeNote.hidden = note.length === 0;
    this.outcome.hidden = false;
  }

  setDebugEnabled(enabled: boolean): void {
    this.debug.hidden = !enabled;
  }

  /** Throttled so the debug overlay never becomes the thing that costs the frame. */
  setDebug(stats: DebugStats, now: number): void {
    if (this.debug.hidden || now - this.lastDebugAt < 200) return;
    this.lastDebugAt = now;
    this.debug.textContent = [
      `fps       ${stats.fps.toFixed(0)}`,
      `particles ${stats.particles}`,
      `magnets   ${stats.magnets}`,
      `physics   ${stats.physicsMs.toFixed(2)} ms`,
      `render    ${stats.renderMs.toFixed(2)} ms`,
      `steps/fr  ${stats.stepsPerFrame}`,
      `draws     ${stats.drawCalls}`,
      `dpr       ${stats.resolution.toFixed(2)}`,
    ].join('\n');
  }
}

/** Retrigger the pop animation, which needs a reflow to restart. */
function pop(element: HTMLElement): void {
  element.classList.remove('is-changed');
  void element.offsetWidth;
  element.classList.add('is-changed');
}

function requireElement(root: ParentNode, selector: string): HTMLElement {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) throw new Error(`Missing UI element: ${selector}`);
  return element;
}
